/**
 * La prenda como matriz de tallas por colores. Docs/34 §10.
 *
 * En el almacén cada talla es un insumo suelto: «Polo básico · M · Negro» es
 * una ficha más entre cien «Polo básico · …», y ver cuántas M quedan de todos
 * los colores era leer la lista entera. El servidor dice qué insumo es cada
 * casilla (`/inventory/variant-families`); cuánto hay de cada uno ya lo trae
 * la lista de insumos, y se cruzan aquí.
 *
 * Las sumas van en milésimas enteras: «0.1 + 0.2» no es «0.3» en coma
 * flotante, y una fila que no suma lo que dicen sus casillas no se cree.
 */

import type { Vocabulario } from './vocabulario';

/** Una prenda con el insumo de cada talla, como la manda el servidor. */
export interface VariantFamily {
  id: string;
  name: string;
  sku: string;
  axes: { name: string; values: string[] }[];
  /**
   * Las temporadas de sus tallas, de la ficha del producto. Un servidor de
   * antes no las manda: entonces no hay filtro de temporada.
   */
  seasons?: string[];
  variants: {
    productId: string;
    name: string;
    attributes: Record<string, string>;
    /** Nulo si la talla no está unida al almacén: no hay estante que contar. */
    supplyId: string | null;
  }[];
}

/** Lo que hace falta de un insumo para llenar la casilla. */
export interface StockDeInsumo {
  id: string;
  sku: string;
  quantity: string;
  unit: string;
  level: 'out' | 'critical' | 'low' | 'ok' | 'over';
}

export interface Casilla {
  productId: string;
  supplyId: string | null;
  sku: string | null;
  /** En milésimas. Cero si no está unida o nunca se recibió nada. */
  milesimas: number;
  nivel: StockDeInsumo['level'] | null;
  /**
   * Lo que espera apartado en alguna venta de la sucursal (el probador), en
   * milésimas. Sigue en el estante —no se ha vendido—, pero la caja ya no la
   * ofrece libre: sin decirlo aquí, el almacén veía «1» de la última M que la
   * caja sabía apartada.
   */
  apartadas: number;
}

export interface Matriz {
  /** El eje de las filas: la talla. */
  ejeFilas: string;
  /** El de las columnas, o nulo si la prenda tiene un solo eje. */
  ejeColumnas: string | null;
  filas: string[];
  /** Con un solo eje, una columna sin nombre. */
  columnas: string[];
  /** `casillas[fila][columna]`; nulo donde la combinación no existe. */
  casillas: (Casilla | null)[][];
  /**
   * Los totales cuentan una talla en negativo como cero, igual que el POS
   * (`totalStockOf` en `product_stock.dart`). Sumada como venía, la M en -2 y
   * la L en 5 daban 3 en el almacén y 5 en la caja: la misma prenda con dos
   * números.
   */
  totalPorFila: number[];
  totalPorColumna: number[];
  total: number;
  /** Las tallas que figuran por debajo de cero: se vendió sin registrar. */
  vendidasDeMas: number;
  /** Lo apartado en toda la prenda, en milésimas. */
  totalApartadas: number;
  unidad: string;
}

/** `"9.06"` → `9060`. */
export function aMilesimas(cantidad: string): number {
  const numero = Number.parseFloat(cantidad);
  return Number.isNaN(numero) ? 0 : Math.round(numero * 1000);
}

/** `9060` → `"9.06"`. */
export function deMilesimas(milesimas: number): string {
  return (milesimas / 1000).toString();
}

export function matrizDe(
  familia: VariantFamily,
  insumos: readonly StockDeInsumo[],
  /** Lo apartado por producto, en milésimas (`GET /held-sales/units`). */
  apartadas: ReadonlyMap<string, number> = new Map(),
): Matriz {
  const porId = new Map(insumos.map((insumo) => [insumo.id, insumo]));
  const [primero, segundo] = familia.axes;
  const filas = primero?.values ?? [];
  const columnas = segundo?.values ?? [''];

  const casillas = filas.map((fila) =>
    columnas.map((columna): Casilla | null => {
      const variante = familia.variants.find(
        (candidata) =>
          candidata.attributes[primero!.name] === fila &&
          (segundo === undefined || candidata.attributes[segundo.name] === columna),
      );
      if (variante === undefined) return null;

      const insumo = variante.supplyId === null ? undefined : porId.get(variante.supplyId);

      return {
        productId: variante.productId,
        supplyId: variante.supplyId,
        sku: insumo?.sku ?? null,
        milesimas: insumo === undefined ? 0 : aMilesimas(insumo.quantity),
        nivel: insumo?.level ?? null,
        apartadas: apartadas.get(variante.productId) ?? 0,
      };
    }),
  );

  const sumar = (lista: (Casilla | null)[]) =>
    lista.reduce((suma, casilla) => suma + Math.max(0, casilla?.milesimas ?? 0), 0);

  const totalPorFila = casillas.map(sumar);
  const totalPorColumna = columnas.map((_, indice) =>
    sumar(casillas.map((fila) => fila[indice] ?? null)),
  );

  const unidad =
    familia.variants
      .map((variante) => (variante.supplyId === null ? undefined : porId.get(variante.supplyId)))
      .find((insumo) => insumo !== undefined)?.unit ?? 'unid';

  return {
    ejeFilas: primero?.name ?? '',
    ejeColumnas: segundo?.name ?? null,
    filas,
    columnas,
    casillas,
    totalPorFila,
    totalPorColumna,
    total: totalPorFila.reduce((suma, valor) => suma + valor, 0),
    vendidasDeMas: casillas.flat().filter((casilla) => casilla !== null && casilla.milesimas < 0)
      .length,
    totalApartadas: casillas.flat().reduce((suma, casilla) => suma + (casilla?.apartadas ?? 0), 0),
    unidad,
  };
}

/**
 * Lo que se va a recibir: una entrada por casilla con cantidad, al insumo de
 * esa talla. Las vacías o en cero no entran; una casilla sin insumo no puede
 * recibir y se devuelve aparte para decirlo.
 */
export function entradasDe(
  matriz: Matriz,
  tecleado: Readonly<Record<string, string>>,
): { entradas: { supplyId: string; productId: string; cantidad: string }[]; sinInsumo: string[] } {
  const entradas: { supplyId: string; productId: string; cantidad: string }[] = [];
  const sinInsumo: string[] = [];

  for (const fila of matriz.casillas) {
    for (const casilla of fila) {
      if (casilla === null) continue;

      const texto = (tecleado[casilla.productId] ?? '').trim().replace(',', '.');
      if (texto === '' || Number.parseFloat(texto) === 0) continue;

      if (casilla.supplyId === null) {
        sinInsumo.push(casilla.productId);
        continue;
      }

      entradas.push({ supplyId: casilla.supplyId, productId: casilla.productId, cantidad: texto });
    }
  }

  return { entradas, sinInsumo };
}

/**
 * Cuántas etiquetas pide cada talla recibida: una por prenda que entró.
 *
 * Una cantidad con decimales —que en ropa no debería pasar— se redondea hacia
 * arriba: sobra una etiqueta antes que quede una prenda sin la suya.
 */
export function etiquetasDe(
  entradas: readonly { productId: string; cantidad: string }[],
): { productId: string; copies: number }[] {
  const porProducto = new Map<string, number>();
  for (const entrada of entradas) {
    const copias = Math.ceil(Number.parseFloat(entrada.cantidad));
    if (!Number.isFinite(copias) || copias <= 0) continue;
    porProducto.set(entrada.productId, (porProducto.get(entrada.productId) ?? 0) + copias);
  }
  return [...porProducto].map(([productId, copies]) => ({ productId, copies }));
}

// ── Lo vendido de cada talla ─────────────────────────────────────────────
//
// La otra mitad de la matriz: qué se vende. Con el stock al lado se ve lo que
// la tienda necesita para reponer —la M negra se vendió doce veces y quedan
// dos; la XL blanca no se movió y quedan nueve—. El servidor da lo vendido de
// cada talla, **neto de lo devuelto** (`/inventory/variant-families/sales`),
// y aquí se cruza con la matriz. Docs/15 §2.50.

/** Lo vendido de una talla en el periodo, como lo manda el servidor. */
export interface VentaDeTalla {
  productId: string;
  /** Neto de lo devuelto. Entero mientras lo sea, como toda cantidad. */
  quantity: number | string;
  /** Nulo sin el permiso de reportes: el almacén ve unidades, no importes. */
  amount: string | null;
  refundedQuantity: number | string;
  refundedAmount: string | null;
}

export interface VentasDePrendas {
  from: string;
  to: string;
  showsAmounts: boolean;
  variants: VentaDeTalla[];
}

export interface CasillaDeVenta {
  productId: string;
  /** Lo vendido neto, en milésimas. */
  vendidas: number;
  /** Lo que volvió, en milésimas. */
  devueltas: number;
  /** En céntimos; nulo sin el permiso de reportes. */
  centimos: number | null;
  /** El estante de la talla, en milésimas, como en la matriz de stock. */
  stock: number;
  /** Si la talla está unida al almacén: sin unir no hay estante que decir. */
  unida: boolean;
}

export interface TotalDeVenta {
  vendidas: number;
  centimos: number | null;
  stock: number;
}

export interface MatrizDeVentas {
  ejeFilas: string;
  ejeColumnas: string | null;
  filas: string[];
  columnas: string[];
  casillas: (CasillaDeVenta | null)[][];
  totalPorFila: TotalDeVenta[];
  totalPorColumna: TotalDeVenta[];
  total: TotalDeVenta;
  conImporte: boolean;
}

/**
 * `"80.00"` → `8000`, sin pasar por la coma flotante: el dinero no es un
 * `number` decimal mientras haya que sumarlo (`0.1 + 0.2`).
 */
export function aCentimos(importe: string): number {
  const texto = importe.trim();
  const negativo = texto.startsWith('-');
  const [enteros = '0', decimales = ''] = texto.replace(/^[-+]/, '').split('.');
  const centimos =
    Number.parseInt(enteros === '' ? '0' : enteros, 10) * 100 +
    Number.parseInt(`${decimales}00`.slice(0, 2), 10);
  return negativo ? -centimos : centimos;
}

/** `8000` → `"80.00"`, como viaja el dinero por la API. */
export function deCentimos(centimos: number): string {
  const signo = centimos < 0 ? '-' : '';
  const absoluto = Math.abs(centimos);
  return `${signo}${Math.floor(absoluto / 100)}.${String(absoluto % 100).padStart(2, '0')}`;
}

export function ventasDe(matriz: Matriz, ventas: VentasDePrendas): MatrizDeVentas {
  const porTalla = new Map(ventas.variants.map((venta) => [venta.productId, venta]));
  const conImporte = ventas.showsAmounts;

  const casillas = matriz.casillas.map((fila) =>
    fila.map((casilla): CasillaDeVenta | null => {
      if (casilla === null) return null;
      const venta = porTalla.get(casilla.productId);

      // La talla que no se vendió no viene del servidor: es un cero, no un hueco.
      return {
        productId: casilla.productId,
        vendidas: venta === undefined ? 0 : aMilesimas(String(venta.quantity)),
        devueltas: venta === undefined ? 0 : aMilesimas(String(venta.refundedQuantity)),
        centimos: !conImporte ? null : venta?.amount == null ? 0 : aCentimos(venta.amount),
        stock: casilla.milesimas,
        unida: casilla.supplyId !== null,
      };
    }),
  );

  const sumar = (lista: (CasillaDeVenta | null)[]): TotalDeVenta => ({
    vendidas: lista.reduce((suma, casilla) => suma + (casilla?.vendidas ?? 0), 0),
    centimos: conImporte ? lista.reduce((suma, casilla) => suma + (casilla?.centimos ?? 0), 0) : null,
    // Lo mismo que la matriz de stock: una talla en negativo cuenta cero.
    stock: lista.reduce((suma, casilla) => suma + Math.max(0, casilla?.stock ?? 0), 0),
  });

  return {
    ejeFilas: matriz.ejeFilas,
    ejeColumnas: matriz.ejeColumnas,
    filas: matriz.filas,
    columnas: matriz.columnas,
    casillas,
    totalPorFila: casillas.map(sumar),
    totalPorColumna: matriz.columnas.map((_, indice) => sumar(casillas.map((fila) => fila[indice] ?? null))),
    total: sumar(casillas.flat()),
    conImporte,
  };
}

/**
 * Una fila por talla de cada prenda, para el CSV: la hoja que la tienda lleva
 * al pedirle al proveedor. Cantidades e importes con punto decimal, como los
 * da la API, para que la hoja los pueda sumar.
 */
export function filasDeVentasCsv(
  prendas: { familia: VariantFamily; ventas: MatrizDeVentas }[],
  palabras: Pick<Vocabulario, 'familiaColumna' | 'ejePorOmision'>,
): { cabecera: string[]; filas: (string | number | null)[][] } {
  // Los ejes con el nombre que les puso la tienda («Talla», «Medida»); el de
  // la primera prenda, que en una tienda suelen ser todos iguales.
  const primera = prendas[0]?.ventas;
  const cabecera = [
    // «Prenda» en la ropa, «Modelo» en tecnología.
    palabras.familiaColumna,
    'SKU',
    primera?.ejeFilas || palabras.ejePorOmision,
    primera?.ejeColumnas ?? 'Color',
    'Vendidas',
    'Devueltas',
    'Importe',
    'Stock',
  ];
  const filas: (string | number | null)[][] = [];

  for (const { familia, ventas } of prendas) {
    ventas.filas.forEach((fila, indiceFila) => {
      ventas.columnas.forEach((columna, indiceColumna) => {
        const casilla = ventas.casillas[indiceFila]?.[indiceColumna];
        if (casilla === null || casilla === undefined) return;

        filas.push([
          familia.name,
          familia.sku,
          fila,
          ventas.ejeColumnas === null ? '' : columna,
          deMilesimas(casilla.vendidas),
          deMilesimas(casilla.devueltas),
          casilla.centimos === null ? null : deCentimos(casilla.centimos),
          // Sin unir no hay estante: vacío, no un cero que diga «agotado».
          casilla.unida ? deMilesimas(casilla.stock) : null,
        ]);
      });
    });
  }

  return { cabecera, filas };
}

/**
 * Lo que falta mandar de una recepción por talla que se cortó a la mitad.
 *
 * Las casillas que ya entraron no se vuelven a mandar aunque se edite su
 * número: con otra operación, corregir 10 por 12 sumaba 12 encima y la talla
 * quedaba en 22. Lo ya recibido se arregla con un ajuste, no recibiendo otra
 * vez.
 */
export function pendientesDe<T extends { productId: string }>(
  entradas: readonly T[],
  entraron: ReadonlySet<string>,
): T[] {
  return entradas.filter((entrada) => !entraron.has(entrada.productId));
}

// ── Contar y pedir por prenda ────────────────────────────────────────────
//
// El conteo y la orden de compra van por insumo, y en una tienda de ropa cada
// insumo es una talla de un color: contar el polo era buscar «Polo básico ·»
// doce veces, y pedirlo, teclear doce filas casi iguales. Con la matriz se
// teclea como se mira el estante —la M de todos los colores en una fila—, y
// lo tecleado sigue yendo al insumo de cada casilla, como siempre.

/**
 * Las prendas que tienen alguna talla entre estos insumos, con solo esas
 * tallas. Un conteo de una categoría no trae la prenda entera: la talla que
 * no está en el conteo no se ofrece para contar, porque al cerrar no se
 * ajustaría.
 */
export function familiasEntre(
  familias: readonly VariantFamily[],
  supplyIds: ReadonlySet<string>,
): VariantFamily[] {
  return familias
    .map((familia) => ({
      ...familia,
      variants: familia.variants.filter(
        (variante) => variante.supplyId !== null && supplyIds.has(variante.supplyId),
      ),
    }))
    .filter((familia) => familia.variants.length > 0);
}

/** Los insumos de las tallas de estas prendas: los que la matriz ya muestra. */
export function insumosDeFamilias(familias: readonly VariantFamily[]): Set<string> {
  return new Set(
    familias.flatMap((familia) =>
      familia.variants.flatMap((variante) => (variante.supplyId === null ? [] : [variante.supplyId])),
    ),
  );
}

// ── Por temporada ─────────────────────────────────────────────────────────
//
// La tienda de ropa cuenta y pide por temporada: en marzo se cuenta lo de
// verano que queda y se pide lo de invierno. La temporada es un campo de la
// ficha (migración 199), y la prenda trae las de sus tallas.

/** Las temporadas que aparecen en estas prendas, en orden. */
export function temporadasDe(familias: readonly VariantFamily[]): string[] {
  return [...new Set(familias.flatMap((familia) => familia.seasons ?? []))].sort((a, b) =>
    a.localeCompare(b, 'es'),
  );
}

/** Las prendas de esa temporada; nula, todas. */
export function deLaTemporada<T extends VariantFamily>(familias: readonly T[], temporada: string | null): T[] {
  if (temporada === null) return [...familias];
  return familias.filter((familia) => (familia.seasons ?? []).includes(temporada));
}
